import { formatearFechaLocal, normalizarFechaISO } from '../../../utils/horaLocal';

// Hook de solo lectura: calcula ganancias a partir de datos inyectados.
// No tiene estado propio — depende de movimientos, gastos, ajustes y productos.
export function useGanancias({ movimientos, gastos, ajustes, productos, esAjusteAnulado, calcularCostoMovimiento }) {

  const obtenerGananciasDiarias = () => {
    const ventasPorDia = {};

    const formatearDia = (fecha) =>
      formatearFechaLocal(normalizarFechaISO(fecha));

    // VENTAS
    movimientos
      .filter(m => (m.tipo === 'VENTA' || (m.tipo === 'SALIDA' && m.subTipo === 'VENTA')) && !m.anulado)
      .forEach(venta => {
        const dia = formatearDia(venta.fecha);

        if (!ventasPorDia[dia]) {
          ventasPorDia[dia] = {
            fecha: dia, fechaObj: venta.fecha,
            compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [],
          };
        }

        const ingreso = venta.cantidad * venta.precio;
        const precioPromedio = calcularCostoMovimiento(venta);
        const costo = venta.cantidad * precioPromedio;
        const gananciaItem = ingreso - costo;

        ventasPorDia[dia].ventas += ingreso;
        ventasPorDia[dia].costos += costo;
        ventasPorDia[dia].ganancia += gananciaItem;

        ventasPorDia[dia].detalles.push({
          productoId: venta.productoId,
          producto: productos.find(p => p.id === venta.productoId)?.nombre || 'N/A',
          cantidad: venta.cantidad,
          precioVenta: venta.precio,
          precioPromedio, ingreso, costo, ganancia: gananciaItem,
        });
      });

    movimientos.filter(m => m.tipo === 'SALIDA' && m.subTipo !== 'VENTA' && !m.anulado).forEach(m => {
      const dia = formatearDia(m.fecha);
      if (!ventasPorDia[dia]) ventasPorDia[dia] = { fecha: dia, fechaObj: m.fecha, compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [] };
      const costo = Number(m.cantidad) * calcularCostoMovimiento(m);
      ventasPorDia[dia].costos += costo;
      ventasPorDia[dia].ganancia -= costo;
      ventasPorDia[dia].detalles.push({ productoId: m.productoId, producto: (productos.find(p => p.id === m.productoId)?.nombre || 'N/A') + ' (Salida)', cantidad: m.cantidad, precioVenta: 0, precioPromedio: calcularCostoMovimiento(m), ingreso: 0, costo, ganancia: -costo });
    });

    // COMPRAS (ENTRADAS)
    movimientos
      .filter(m => m.tipo === 'ENTRADA' && !m.anulado)
      .forEach(entrada => {
        const dia = formatearDia(entrada.fecha);

        if (!ventasPorDia[dia]) {
          ventasPorDia[dia] = {
            fecha: dia, fechaObj: entrada.fecha,
            compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [],
          };
        }

        ventasPorDia[dia].compras += entrada.cantidad * entrada.precio;
      });

    // GASTOS
    (gastos || [])
      .filter(g => g.estado !== 'ANULADO')
      .forEach(gasto => {
        const dia = formatearDia(gasto.fecha);

        if (!ventasPorDia[dia]) {
          ventasPorDia[dia] = {
            fecha: dia, fechaObj: gasto.fecha,
            compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [],
          };
        }

        ventasPorDia[dia].gastos += gasto.monto;
        ventasPorDia[dia].ganancia -= gasto.monto;
      });

    // AJUSTES
    (ajustes || [])
      .filter(a => !esAjusteAnulado(a))
      .forEach(ajuste => {
        const dia = formatearDia(ajuste.fecha);

        if (!ventasPorDia[dia]) {
          ventasPorDia[dia] = {
            fecha: dia, fechaObj: ajuste.fecha,
            compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [],
          };
        }

        const montoAjuste = ajuste.tipo === 'SOBRANTE' ? ajuste.monto : (ajuste.tipo === 'FALTANTE' ? -ajuste.monto : 0);
        ventasPorDia[dia].ajustes += montoAjuste;
        ventasPorDia[dia].ganancia += montoAjuste;
      });

    // INYECCIONES
    (ajustes || [])
      .filter(a => !esAjusteAnulado(a))
      .forEach(ajuste => {
        const dia = formatearDia(ajuste.fecha);

        if (!ventasPorDia[dia]) {
          ventasPorDia[dia] = {
            fecha: dia, fechaObj: ajuste.fecha,
            compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [],
          };
        }

        const montoAjuste = ajuste.tipo === 'INYECCIÓN' ? ajuste.monto : (ajuste.tipo === 'RETIRO' ? -ajuste.monto : 0);
        ventasPorDia[dia].inyecciones += montoAjuste;
      });

    return Object.values(ventasPorDia).sort(
      (a, b) => new Date(b.fechaObj) - new Date(a.fechaObj)
    );
  };

  const obtenerGananciasPorFecha = (fecha) => {
    const formatoFecha = formatearFechaLocal(normalizarFechaISO(fecha));
    const ganancias = obtenerGananciasDiarias();
    return ganancias.find(g => g.fecha === formatoFecha) || {
      fecha: formatoFecha,
      compras: 0, ventas: 0, costos: 0, gastos: 0, ajustes: 0, inyecciones: 0, ganancia: 0, detalles: [],
    };
  };

  return {
    obtenerGananciasDiarias,
    obtenerGananciasPorFecha,
  };
}
